import * as B from '@blueprintjs/core';
import type {IconName} from '@blueprintjs/icons';
import React from 'react';

import {useUserInfo} from 'app/providers/AuthProvider';
import * as C from 'app/utils/constants';
import {atLeastUserRole} from 'app/utils/userUtils';

import {useUpdateAgentSettings} from '../hooks';
import cs from './AgentExplorerSplash.module.scss';

const TERMS_URL = 'https://lens.tech/terms/';

interface SplashFeatureLabel {
  icon: IconName;
  title: string;
  description: string;
}

/**
 * First-run "splash" view for the Agent sidebar: introduces the Lens Agent and
 * hands off to the Prompt view, with the ability for owners to enable it for the org.
 */
const AgentExplorerSplash: React.FC<{onGetStarted: () => void}> = ({onGetStarted}) => {
  const [organization, profile] = useUserInfo();

  // The org's total agent run quota drives the "N free runs" feature copy.
  const agentSettings = React.useMemo(
    () => organization.getIn(['settings', C.ORG_SETTING_AGENT_SETTINGS])?.toJS(),
    [organization]
  ) as {run_quota: number; enabled: boolean; hidden: boolean} | undefined;

  const runQuota = agentSettings?.run_quota ?? C.AGENT_DEFAULT_RUN_QUOTA;

  const isOwner = atLeastUserRole(C.USER_ROLE_OWNER, profile);
  const agentEnabledForOrg = !!agentSettings?.enabled;

  const updateAgentSettings = useUpdateAgentSettings(organization.get('id'));

  // Until the Agent is enabled for the org, only owners can access this button,
  // which enables it for the org. Once enabled, the button is a plain "try it" hand-off to the Prompt view.
  const showEnableCta = isOwner && !agentEnabledForOrg;
  const showAskYourOwnerCta = !isOwner && !agentEnabledForOrg;

  const onClick = () => {
    if (showEnableCta) {
      updateAgentSettings.mutate({enabled: true});
    }
    onGetStarted();
  };

  const features: SplashFeatureLabel[] = [
    {
      icon: 'office',
      title: 'Identify structures',
      description: 'Find and outline buildings on a property',
    },
    {
      icon: 'comment',
      title: 'Describe a scene',
      description: 'Summarize site conditions in plain language',
    },
    {
      icon: 'comparison',
      title: 'Detect changes',
      description: 'Pinpoint property changes over time',
    },
    {
      icon: 'time',
      title: runQuota !== undefined ? `${runQuota} free runs` : 'Free runs',
      description: 'Explore on any property',
    },
  ];

  return (
    <div className={cs.splashLayout}>
      <div className={cs.hero}>
        <span className={cs.heroIcon}>
          <B.Icon icon="clean" size={32} />
        </span>

        <h3 className={cs.heroTitle}>Meet the Lens Agent</h3>
        <span className={cs.pill}>FREE TRIAL</span>

        <p className={cs.heroDescription}>
          Run a prompt on a property and the Agent will review your selected imagery, draft
          observations, and focus your attention where it matters.
        </p>
        <a
          href="https://lens.tech/support/article/lens-agent"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>

        <div className={cs.features}>
          {features.map((feature) => (
            <div key={feature.title} className={cs.feature}>
              <span className={cs.featureIcon}>
                <B.Icon icon={feature.icon} />
              </span>
              <span className={cs.featureText}>
                <span className={cs.featureTitle}>{feature.title}</span>
                <span className={cs.featureDescription}>{feature.description}</span>
              </span>
            </div>
          ))}
        </div>

        {(showEnableCta || agentEnabledForOrg) && (
          <B.Tooltip content="Enables for your whole org" disabled={!showEnableCta}>
            <B.Button
              className={cs.getStartedButton}
              text={showEnableCta ? `Enable for ${organization.get('name')}*` : 'Try it now'}
              icon="clean"
              intent="primary"
              large
              loading={updateAgentSettings.isPending}
              onClick={onClick}
            />
          </B.Tooltip>
        )}

        {showEnableCta && (
          <>
            <B.Button
              minimal
              disabled={updateAgentSettings.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'This will hide the Lens Agent for everyone in your team. You can enable it later from Settings. Are you sure you want to proceed?'
                  )
                ) {
                  updateAgentSettings.mutate({hidden: true});
                }
              }}
            >
              Not now
            </B.Button>

            <p className={cs.termsNotice}>
              *We've made a few updates to our terms to reference this new capability. You agree
              that clicking the button above and turning on the Agent constitutes your written
              acceptance, on behalf of your organization, to the updated{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
              .
            </p>
          </>
        )}

        {showAskYourOwnerCta && (
          <B.Callout>
            Reach out to your Lens account owner to enable this for your organization!
          </B.Callout>
        )}
      </div>
    </div>
  );
};

export default AgentExplorerSplash;
